import React from 'react';
import { Tabs } from 'antd';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { ChannelConfig } from '../../../types/ad-channels';
import { PreviewsMap } from '../../../types/demo-previews';

interface AdChannelTabsProps {
  selectedResolution: string | null;
  onResolutionChange: (resolution: string) => void;
  availableChannels: string[];
  channelConfigs: Record<string, ChannelConfig>;
  getPreviewsForChannel: (channel: string) => PreviewsMap;
}

export const AdChannelTabs: React.FC<AdChannelTabsProps> = ({
  selectedResolution,
  onResolutionChange,
  availableChannels,
  channelConfigs,
  getPreviewsForChannel
}) => {
  const { fontSize, spacing } = useResponsiveSizes();

  const [selectedChannel, setSelectedChannel] = React.useState<string>(availableChannels[0]);

  // Obtener resoluciones por canal
  const resolutionsByChannel = React.useMemo(() => {
    const grouped: Record<string, string[]> = {};

    availableChannels.forEach(channel => {
      const previews = getPreviewsForChannel(channel);
      grouped[channel] = Object.keys(previews);
    });

    return grouped;
  }, [availableChannels, getPreviewsForChannel]);

  // Si el canal seleccionado ya no está disponible, cambiar al primero
  React.useEffect(() => {
    if (!availableChannels.includes(selectedChannel) && availableChannels.length > 0) {
      setSelectedChannel(availableChannels[0]);
    }
  }, [availableChannels, selectedChannel]);

  const getButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    height: 30,
    padding: '0px 20px',
    border: isSelected ? 'none' : '1px solid #d1d5db',
    borderRadius: 24,
    background: isSelected ? 'rgb(72 114 207)' : 'white',
    color: isSelected ? 'white' : '#374151',
    fontSize: fontSize.base,
    fontWeight: isSelected ? 500 : 400,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    boxShadow: isSelected ? '0 4px 12px rgba(37, 99, 235, 0.4)' : 'none'
  });

  const tabItems = availableChannels.map(channel => {
    const config = channelConfigs[channel];

    return {
      key: channel,
      label: <span style={{ fontSize: fontSize.base, fontWeight: 500 }}>{config.label}</span>,
      children: (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', paddingTop: 8 }}>
          {resolutionsByChannel[channel]?.map(res => {
            const isSelected = selectedResolution === res;
            return (
              <button
                key={res}
                onClick={() => onResolutionChange(res)}
                style={getButtonStyle(isSelected)}
                onMouseEnter={e => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#60a5fa';
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                  }
                }}
                onMouseLeave={e => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                {res}
              </button>
            );
          })}
        </div>
      )
    };
  });

  return (
    <div style={{ width: '100%', marginBottom: spacing.lg }}>
      <Tabs
        activeKey={selectedChannel}
        onChange={key => setSelectedChannel(key)}
        items={tabItems}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      />
    </div>
  );
};
